import {useCallback} from 'react';

/**
 * Hook for keyboard navigation in menu components
 * Provides arrow key navigation, Enter activation, and escape handling
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.open - Whether the menu is open
 * @param {boolean} options.isClosing - Whether the menu is closing
 * @param {Array} options.items - Array of items to navigate
 * @param {number} options.highlightedIndex - Current highlighted index
 * @param {Function} options.setHighlightedIndex - Function to set highlighted index
 * @param {Function} options.onItemActivate - Callback when item is activated (Enter/Space)
 * @param {Function} options.onEscape - Callback when Escape is pressed
 * @param {Function} [options.getItemDisabled] - Function to check if item is disabled: (item, index) => boolean
 * @param {Function} [options.onHome] - Callback when Home is pressed (defaults to jump to first)
 * @param {Function} [options.onEnd] - Callback when End is pressed (defaults to jump to last)
 * @returns {Function} Keyboard event handler
 */
export const useMenuKeyboard = ({
	open,
	isClosing,
	items = [],
	highlightedIndex = 0,
	setHighlightedIndex,
	onItemActivate,
	onEscape,
	getItemDisabled = (item, index) => item?.disabled === true,
	onHome,
	onEnd,
}) => {
	const handleKeyDown = useCallback(
		e => {
			if (!open || isClosing) return;

			const enabledItems = items.filter((item, idx) => !getItemDisabled(item, idx));
			if (enabledItems.length === 0) {
				// If no enabled items, only handle Escape
				if (e.key === 'Escape') {
					e.preventDefault();
					onEscape?.();
				}
				return;
			}

			// Find current highlighted item's index in enabled items
			let currentEnabledIndex = enabledItems.findIndex(
				(_, idx) => items.indexOf(enabledItems[idx]) === highlightedIndex,
			);
			if (currentEnabledIndex === -1) {
				currentEnabledIndex = 0; // Fallback to first enabled item
			}

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				const nextEnabledIndex = (currentEnabledIndex + 1) % enabledItems.length;
				const nextItem = enabledItems[nextEnabledIndex];
				const nextItemIndex = items.indexOf(nextItem);
				setHighlightedIndex(nextItemIndex);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				const prevEnabledIndex =
					(currentEnabledIndex - 1 + enabledItems.length) % enabledItems.length;
				const prevItem = enabledItems[prevEnabledIndex];
				const prevItemIndex = items.indexOf(prevItem);
				setHighlightedIndex(prevItemIndex);
			} else if (e.key === 'Home') {
				e.preventDefault();
				if (onHome) {
					onHome();
				} else {
					// Default: jump to first enabled item
					const firstEnabledItem = enabledItems[0];
					if (firstEnabledItem) {
						const firstIndex = items.indexOf(firstEnabledItem);
						setHighlightedIndex(firstIndex);
					}
				}
			} else if (e.key === 'End') {
				e.preventDefault();
				if (onEnd) {
					onEnd();
				} else {
					// Default: jump to last enabled item
					const lastEnabledItem = enabledItems[enabledItems.length - 1];
					if (lastEnabledItem) {
						const lastIndex = items.indexOf(lastEnabledItem);
						setHighlightedIndex(lastIndex);
					}
				}
			} else if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				const currentItem = items[highlightedIndex];
				if (currentItem && !getItemDisabled(currentItem, highlightedIndex)) {
					onItemActivate?.(currentItem, highlightedIndex);
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				onEscape?.();
			}
		},
		[
			open,
			isClosing,
			items,
			highlightedIndex,
			setHighlightedIndex,
			onItemActivate,
			onEscape,
			getItemDisabled,
			onHome,
			onEnd,
		],
	);

	return handleKeyDown;
};
